import { useEffect, useState } from "react";
import { AccountType } from "../shared/types/accounts";
import { User } from "../features/user";

export enum FeatureName {
  NewAddAccountModal = "NewAddAccountModal",
  MenuColorOption1 = "MenuColorOption1",
  MenuColorOption2 = "MenuColorOption2",
  SocialListening = "SocialListening",
  SLGMB = "SLGMB",
  CaptionV2 = "CaptionV2",
}

class LocalStorageFeatureToggle {
  set(featureName: `${FeatureName}`, value: boolean) {
    this.validateFeatureName(featureName);
    if (value) {
      window.localStorage.setItem(featureName, "1");
    } else {
      window.localStorage.removeItem(featureName);
    }
  }

  get(featureName: `${FeatureName}`) {
    return !!window.localStorage.getItem(featureName);
  }

  private validateFeatureName(featureName: string) {
    if (!Object.values(FeatureName).includes(featureName as FeatureName)) {
      throw new Error(`Invalid feature name: ${featureName}`);
    }
  }
}

class Deferred<T> {
  public promise: Promise<T>;
  private resolveFunction!: (value: T | PromiseLike<T>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rejectFunction!: (reason?: any) => void;

  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.resolveFunction = resolve;
      this.rejectFunction = reject;
    });
  }

  resolve(value: T | PromiseLike<T>) {
    this.resolveFunction(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reject(reason?: any) {
    this.rejectFunction(reason);
  }
}

type AdminPanelToggleName = "none";

class AdminPanelFeatureToggle {
  private toggleContextDeferred = new Deferred<Record<string, boolean>>();
  private toggleContext: Record<string, boolean> = {};

  async get(name: AdminPanelToggleName): Promise<boolean> {
    const toggles = await this.toggleContextDeferred.promise;
    return !!toggles[name];
  }

  syncGet(name: AdminPanelToggleName): boolean {
    return !!this.toggleContext[name];
  }

  public notifyToggleContext(toggles: Record<string, boolean> | undefined) {
    console.log("notifyToggleContext", toggles);
    this.toggleContext = toggles || {};
    this.toggleContextDeferred.resolve(toggles || {});
  }

  public notifyFromAccounts(users: AccountType[] | undefined) {
    /** make sure this function does not block any processing */
    setTimeout(() => {
      const found = users?.find(
        (user) => (user.platformType as string) !== "BRANDGROUPSUMMARY"
      );
      if (found?.featureToggles) {
        this.notifyToggleContext(found.featureToggles);
      } else {
        this.notifyToggleContext({});
      }
    }, 0);
  }
}

export const adminPanelFeatureToggle = new AdminPanelFeatureToggle();

export const useAdminPanelToggle = (
  name: AdminPanelToggleName,
  localToggleName?: `${FeatureName}`
) => {
  const [value, setValue] = useState<boolean | undefined>(
    adminPanelFeatureToggle.syncGet(name)
  );
  useEffect(() => {
    adminPanelFeatureToggle.get(name).then((enabled) => {
      if (enabled !== value) {
        setValue(enabled);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  if (localToggleName && localStorageFeatureToggle.get(localToggleName)) {
    return true;
  }

  return value;
};

class EntitlementFeatureToggle {
  public entitlements: Record<string, string> | undefined;
  public overrideEntitlements: Record<string, string> | undefined;
  notifyUser(user: User) {
    if (user?.preferences?.entitlements) {
      this.entitlements = { ...user.preferences.entitlements };
    }
  }

  get(entitlement: "bulk-create") {
    if (this.overrideEntitlements?.[entitlement] !== undefined) {
      return this.overrideEntitlements?.[entitlement] === "true";
    }
    return this.entitlements?.[entitlement] === "true";
  }

  override(entitlements: Record<string, string> | undefined) {
    this.overrideEntitlements = entitlements;
  }
}

export const entitlementFeatureToggle = new EntitlementFeatureToggle();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).entitlementFeatureToggle = entitlementFeatureToggle;

export const localStorageFeatureToggle = new LocalStorageFeatureToggle();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).localStorageFeatureToggle = localStorageFeatureToggle;
